<script>
  import { tick } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { browser } from '$app/environment';
  import sanitizeHtml from 'sanitize-html';

  import jQuery from 'jquery';

  import Autolinker from 'autolinker';

  const dispatch = createEventDispatcher();

  export let textEl = null;
  export let editable = false; // can we edit?
  export let placeholder = null;
  export let text;
  export let lines = 1;
  export let maxHeight = null;
  export let linkClicked = () => {};
  export let useAutoLinker = true;
  export let expand = true; // can we expand this? (only if not editable)
  export let editor = false;
  export let mobile = false;
  export let focus = false;
  export let collapseAfterExpand = true; // once the text is expanded, should it collapse?
  export let exportTextOnly = false;
  export let documentId = null;
  export let textLength;
  export let heartEmoji = false;
  export let starEyesEmoji = false;
  export let initialTextLength;

  // Update the styles to display the correct number of lines whenever `lines` changes.
  $: if (lines) {
    applyMaxLinesStyle();
  }

  export let shaved = false; // updates whenever text is shaved / unshaved
  export let shavedOnFirstLoad = false;

  let maxLinesStyle = '';
  let maxHeightStyle = maxHeight ? `max-height: ${maxHeight}px;` : '';
  let setupDone = false;

  export let ckEditor = null;

  export let toggleExpandContent = async function () {
    if (expand && !hasSelectedText()) {
      if (isShaved()) {
        removeMaxLinesStyle();
      } else {
        applyMaxLinesStyle();
      }
      await tick();
      shaved = isShaved();
    }
  };

  let isShaved = function () {
    return textEl.scrollHeight > textEl.clientHeight;
  };

  let hasSelectedText = function () {
    var sel = false;

    try {
      sel = getSelection().toString();
    } catch {
      sel = false;
    }

    return sel;
  };

  export const resetEditorValue = async function () {
    if (editor) {
      setText('');
    }
  };

  let decodeEntities = function (encodedString) {
    var textArea = document.createElement('textarea');
    textArea.innerHTML = encodedString;
    return textArea.value;
  };

  let applyMaxLinesStyle = function () {
    if (collapseAfterExpand) {
      maxLinesStyle = `display: -webkit-inline-box; -webkit-box-orient: vertical; -webkit-line-clamp: ${lines}; overflow: hidden;`;
    }
  };

  let removeMaxLinesStyle = function () {
    maxLinesStyle = '';
  };

  export const focusElement = function () {
    if (ckEditor) {
      ckEditor.editing.view.focus();
    } else if (textEl) {
      textEl.focus();
    }
    focusElementHelper();
  };

  let focusElementHelper = async function () {
    removeMaxLinesStyle();
    dispatch('focus', { text: text });
    jQuery(textEl).addClass('editable-focused');
    await tick();
    shaved = isShaved();
  };

  let focusedElement = function (e) {
    setTimeout(() => (textLength = jQuery(textEl).text().length), 0);

    if (!editor) {
      focusElementHelper();
    }

    jQuery(e.target).attr('spellcheck', true);
  };

  let blurElementHelper = async function () {
    applyMaxLinesStyle();
    jQuery(textEl).scrollTop(0);
    jQuery(textEl).removeClass('editable-focused');
    await tick();
    shaved = isShaved();
  };

  let blurElement = function (e) {
    if (!editor) {
      blurElementHelper();

      if (exportTextOnly) {
        text = jQuery(textEl).text();
      } else {
        text = decodeEntities(jQuery(textEl).html());
      }

      dispatch('blur', { text: text });
    }

    jQuery(e.target).removeAttr('spellcheck');
  };

  let setContentEditableForTextOnly = function (node) {
    jQuery(node).on('paste', function (e) {
      e.preventDefault();

      var text = '';

      if (e.clipboardData || e.originalEvent.clipboardData) {
        text = (e.originalEvent || e).clipboardData.getData('text/plain');
      } else if (window.clipboardData) {
        text = window.clipboardData.getData('Text');
      }

      if (document.queryCommandSupported('insertText')) {
        document.execCommand('insertText', false, text);
      } else {
        document.execCommand('paste', false, text);
      }
    });
  };

  let setupEnterPressForContentEditable = function (node) {
    jQuery(node).on('keypress', function (e) {
      if ((e.which == 13 || e.keyCode == 13) && !e.shiftKey) {
        e.preventDefault();
        jQuery(node).blur();
        dispatch('enter');
      } else if (e.which == 13 || (e.keyCode == 13 && e.shiftKey)) {
        // do nothing. pre takes care of this!
      }
    });

    jQuery(node).on('keydown', function (e) {
      setTimeout(() => (textLength = jQuery(textEl).text().length), 10);

      if (e.which == 9 || e.keyCode == 9) {
        e.stopPropagation();
        e.preventDefault();
        jQuery(node).blur();

        if (e.shiftKey) {
          dispatch('tab-pressed-shift');
        } else {
          dispatch('tab-pressed');
        }
      }
      if ((e.which == 13 || e.keyCode == 13) && !e.shiftKey) {
        // prevent custom action when mentions is chosen
        if (jQuery('.ck-mentions .ck-list__item').length == 0) {
          e.preventDefault();
          jQuery(node).blur();
          dispatch('enter-submit');
        }
      }
    });
  };

  let setupEditor = async function (node) {
    if (editor && browser) {
      const Editor = await import('@utils/editor');
      const hasMaxLinesStyle = maxLinesStyle !== '';

      removeMaxLinesStyle();

      if (mobile) {
        ckEditor = await Editor.default.setupMobile(dispatch, node, {
          placeholder: placeholder,
        });
      } else {
        ckEditor = await Editor.default.setup(dispatch, node, {
          placeholder: placeholder,
        });
      }

      if (hasMaxLinesStyle) {
        applyMaxLinesStyle();
      }

      if (ckEditor) {
        ckEditor.ui.focusTracker.on('change:isFocused', (evt, name, isFocused) => {
          if (isFocused) {
            focusElementHelper();
          } else {
            blurElementHelper();
            text = ckEditor.getData();
            dispatch('blur', { text: text });
          }
        });

        ckEditor.model.document.on('change:data', () => {
          setTimeout(() => (textLength = jQuery(textEl).text().length), 10);
        });

        jQuery(node).on('keydown', function (e) {
          // space key, problem with CKEditor content starting with a space
          if (e.which == 32 || e.keyCode == 32) {
            let content = ckEditor.getData();
            if (content == '') {
              e.stopPropagation();
              e.preventDefault();
            }
          }
        });

        if (focus) {
          ckEditor.editing.view.focus();
        }
        dispatch('editor-ready');
      }
    }
  };

  let setTextAction = function (node, text) {
    textEl = node;

    var setup = function () {
      setText(text);
      applyMaxLinesStyle();

      if (!editable) {
        setupClickableLinks(node);
      }
    };

    if (editor) {
      setText('');
      setTimeout(setup, 0);
    } else {
      setup();
    }

    return {
      update(text) {
        setText(text);
      },
    };
  };

  let setupClickableLinks = function (node) {
    jQuery(node)
      .find('a')
      .each(function () {
        jQuery(this).on('click', function (e) {
          let url = jQuery(this).prop('href');

          e.preventDefault();
          e.stopPropagation();
          linkClicked(url);
          window.open(url);
        });
      });
  };

  let addEmoji = function (emoji) {
    setText(jQuery(textEl).text() + emoji);
    jQuery(textEl).focus();

    setTimeout(() => {
      if (ckEditor) {
        ckEditor.model.change((writer) => {
          writer.setSelection(writer.createPositionAt(ckEditor.model.document.getRoot(), 'end'));
        });

        ckEditor.focus();
      }
    }, 0);
  };

  let setText = async function (newText) {
    let content = newText;

    let setShaved = function () {
      shaved = isShaved();
      shavedOnFirstLoad = shaved;
    };

    // Handle empty content
    if (!content || content.trim() === '') {
      jQuery(textEl).html('');
    }

    if (useAutoLinker) {
      content = Autolinker.link(newText, {
        replaceFn: function (match) {
          return `<a rel='nofollow' href='${match.getAnchorHref()}'>${match.getAnchorText()}</a>`;
        },
      });
    }

    if (ckEditor) {
      ckEditor.setData(content);
      setTimeout(setShaved, 100);
    } else {
      await jQuery(textEl).html(content).promise();
      setShaved();
    }
  };

  const setupElement = function (el) {
    if (!editor) {
      // var el = jQuery(e.target).closest('.text-widget')[0];
      if (!setupDone) {
        setContentEditableForTextOnly(el);
        setupEnterPressForContentEditable(el);
        setupDone = true;
      }
    }
  };

  const setTextServerSide = function (htmlContent, maxCharacters = 250) {
    return sanitizeHtml(htmlContent, {
      textFilter: function (text) {
        if (text.length > maxCharacters) {
          return text.substring(0, maxCharacters) + '...';
        }
        return text;
      },
    });
  };
</script>

<style style="text/scss" lang="scss">
  :root {
    --ck-color-button-on-color: #f9f7ff !important;
  }
  :global(.ck-body-wrapper.mobile-list) {
    :global(.ck-mentions) {
      position: fixed;
      left: 0px;
      width: 100%;
      bottom: 64px;
    }
  }
  :global(.ck-balloon-panel) {
    border: 0px !important;
  }
  :global(.ck-mentions) {
    background-color: #fff;
    width: inherit;
    box-shadow: 0px 22px 100px 0px rgba(0, 0, 0, 0.1);
    border-bottom-right-radius: 15px;
    border-bottom-left-radius: 15px;
    :global(.ck-list__item) {
      // border: 2px solid black;
    }
  }

  :global(.ck-mentions::-webkit-scrollbar-thumb) {
    border-radius: 8px !important;
    background-color: #d6cfea !important;
    min-height: 57px !important;
    background-clip: padding-box !important;
  }

  :global(.ck-mentions::-webkit-scrollbar-track) {
    border-radius: 5px !important;
  }
  :global(.ck-mentions::-webkit-scrollbar) {
    width: 6px !important;
    background-color: white !important;
  }

  .text-widget {
    display: inline-block;
    white-space: pre-wrap; /* css-3 */
    white-space: -moz-pre-wrap; /* Mozilla, since 1999 */
    white-space: -pre-wrap; /* Opera 4-6 */
    white-space: -o-pre-wrap; /* Opera 7 */
    word-wrap: break-word; /* Internet Explorer 5.5+ */
    :global(a.mention) {
      color: #a893f9 !important;
      font-size: 14px !important;
      font-style: normal !important;
      font-weight: 500 !important;
      line-height: 20px !important;
      text-decoration: none !important;
    }
    :global(a) {
      // color: #5e518e !important;
      color: #a893f9 !important;
      text-decoration: underline !important;
      background: transparent !important;
      font-size: 14px !important;
      font-style: normal !important;
      font-weight: 500 !important;
      line-height: 20px !important;
    }
  }

  .uneditable-content {
    overflow: inherit;
    word-break: break-word;
  }

  .editable-focused {
    background: #ede9ff;
    opacity: 1;
    border: 2px solid var(--editable-focused-border-color, #a893f9) !important;
    cursor: text;
    caret-color: #a893f9;
    outline: none !important;
    overflow-y: auto;
  }

  .editable-content {
    border: 2px solid transparent;
    overflow: hidden;
    width: 100%;
    padding: 4px;
    margin: -6px;
    border-radius: 7px !important;
    box-shadow: unset !important;

    &:hover {
      background-color: #ede9ff;
      cursor: text;
      outline: none;
      caret-color: #a893f9;
      opacity: 1;
    }

    &:focus {
      background: #ede9ff;
      opacity: 1;
      border: 2px solid var(--editable-focused-border-color, #a893f9) !important;
      cursor: text;
      caret-color: #a893f9;
      outline: none !important;
      overflow-y: auto;
    }
  }

  .editable-content:empty:before {
    content: attr(placeholder);
    color: black;
    opacity: 0.5;
  }

  .pointer {
    cursor: pointer;
  }

  .truncate-text {
    display: -webkit-box;
    -webkit-line-clamp: 1; /* Number of lines to display before truncating */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal; /* or 'pre-wrap' if you want to preserve whitespace */
  }

  .emoji-container {
    margin-top: 0px;
    margin-right: 6px;
    display: flex;
    gap: 10px;

    .emoji-icon {
      font-size: 16px;
      font-weight: bold;
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }
</style>

{#if !browser}
  <span
    class="{initialTextLength ? '' : 'truncate-text'} text-widget uneditable-content {$$props.class}"
    style="-webkit-line-clamp: {lines}; {$$props.style || ''} {maxHeightStyle} {maxLinesStyle}"
    >{@html setTextServerSide(text, initialTextLength)}</span>
{:else if editable}
  <span
    id={documentId}
    use:setTextAction={text}
    use:setupEditor
    use:setupElement
    on:click|preventDefault|stopPropagation={() => {}}
    on:paste={() => dispatch('paste')}
    class="text-widget editable-content {$$props.class}
    {$$props.editableClass}"
    contenteditable
    on:focus={focusedElement}
    on:blur={blurElement}
    on:keydown
    style="{$$props.style || ''} {maxHeightStyle} {maxLinesStyle}" />
  {#if (!textLength || textLength == 0) && (heartEmoji || starEyesEmoji)}
    <div class="emoji-container">
      {#if heartEmoji}
        <div class="emoji-icon" on:click|preventDefault|stopPropagation={() => addEmoji('❤️')}>❤️</div>
      {/if}
      {#if starEyesEmoji}
        <div class="emoji-icon" on:click|preventDefault|stopPropagation={() => addEmoji('🤩')}>🤩</div>
      {/if}
    </div>
  {/if}
{:else}
  <span
    use:setTextAction={text}
    on:click|preventDefault={(e) => {
      toggleExpandContent();
      dispatch('click', { event: e });
    }}
    class:pointer={shavedOnFirstLoad}
    style="{$$props.style || ''} {maxHeightStyle} {maxLinesStyle}"
    class="text-widget uneditable-content {$$props.class}
    {$$props.uneditableClass}" />
{/if}
